import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import cookie from "react-cookies";
import CurrencyFormat from "react-currency-format";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import PlacesAutocomplete from "../../components/common/PlacesAutocomplete";
import constants from "../../Constants";
import util from "../../util";
import "./multi-stop-book.css";

const baseURL = constants.baseURL;
const TRACK_BASE = "https://qiktruck.co.za/track-order";

const STOP_TYPES = [
  { value: "waypoint", label: "General stop" },
  { value: "pickup", label: "Extra pickup" },
  { value: "delivery", label: "Extra drop-off" },
];

const emptyLocation = (role = "waypoint") => ({
  id: `${Date.now()}-${Math.random()}`,
  role,
  address: "",
  lat: null,
  lng: null,
  stopType: role === "pickup" ? "pickup" : role === "delivery" ? "delivery" : "waypoint",
  contactName: "",
  contactPhone: "",
  accessNotes: "",
});

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function shortAddress(address) {
  if (!address) return "";
  return address.split(",")[0];
}

function locationLabel(loc, index, total) {
  if (loc.role === "pickup" || index === 0) return "Start · collection";
  if (loc.role === "delivery" || index === total - 1) return "End · final delivery";
  return `Stop ${index}`;
}

function swapPair(arr, i, j) {
  const next = [...arr];
  [next[i], next[j]] = [next[j], next[i]];
  return next;
}

function locationPayload(loc) {
  return {
    lat: loc.lat,
    lng: loc.lng,
    address: loc.address,
    notes: loc.accessNotes,
    contact_name: loc.contactName,
    contact_phone: loc.contactPhone,
  };
}

function authHeaders() {
  return {
    "Content-Type": "application/json",
    Authorization: cookie.load("session-id-qt"),
  };
}

function bookableVehicleTypes(vehicles) {
  return (vehicles || [])
    .filter((v) => {
      const type = String(v.type || v.vehicletype || "");
      return (v.sell || v.display) && type.indexOf("tow") === -1;
    })
    .sort((a, b) => (a.sortOrder ?? 999) - (b.sortOrder ?? 999));
}

function vehicleTypeSlug(v) {
  return v.type || v.vehicletype || "";
}

function vehicleOptionLabel(v) {
  if (v.displaytype) return v.displaytype;
  if (v.bodylabel) return v.bodylabel;
  return vehicleTypeSlug(v)
    .replace("open", "")
    .replace("closed", "")
    .replace("andtrailer", "")
    .trim();
}

function vehicleImageUrl(slug) {
  return `https://media.qiktruck.co.za/3d-${slug}.png`;
}

function vehicleShortName(v) {
  const slug = vehicleTypeSlug(v);
  return slug
    .replace("open", "")
    .replace("closed", "")
    .replace("fridge", " fridge")
    .replace("andtrailer", " + trailer")
    .replace("tipper", " tipper")
    .trim();
}

function vehicleCapacityLabel(v) {
  const tonnes = Number(v.tonnes);
  if (!Number.isFinite(tonnes) || tonnes <= 0) return "";
  if (tonnes < 1) return `${Math.round(tonnes * 1000)} kg`;
  return `${tonnes} ton`;
}

function vehicleBodyKind(v) {
  const slug = vehicleTypeSlug(v).toLowerCase();
  if (slug.includes("fridge")) return "Refrigerated";
  if (slug.includes("tipper")) return "Tipper";
  if (slug.includes("bike")) return "Bike";
  if (slug.includes("closed")) return "Closed";
  if (slug.includes("open")) return "Open";
  return v.bodylabel || "Vehicle";
}

function vehicleTipExamples(v) {
  const tip = v.tip || {};
  return tip.examples || tip.drivertip || "";
}

const VEHICLE_GROUPS = [
  {
    title: "Open vehicles",
    filter: (v) => {
      const t = vehicleTypeSlug(v);
      return (
        t.indexOf("closed") === -1 &&
        t.indexOf("bike") === -1 &&
        t.indexOf("tipper") === -1 &&
        t.indexOf("fridge") === -1
      );
    },
  },
  {
    title: "Closed vehicles",
    filter: (v) => {
      const t = vehicleTypeSlug(v);
      return (
        t.indexOf("open") === -1 &&
        t.indexOf("bike") === -1 &&
        t.indexOf("tipper") === -1 &&
        t.indexOf("fridge") === -1
      );
    },
  },
  {
    title: "Refrigerated vehicles",
    filter: (v) => vehicleTypeSlug(v).indexOf("fridge") > -1,
  },
  {
    title: "Other vehicles",
    filter: (v) => {
      const t = vehicleTypeSlug(v);
      return t.indexOf("bike") > -1 || t.indexOf("tipper") > -1;
    },
  },
];

export default function MultiStopBookSection() {
  const [locations, setLocations] = useState([
    emptyLocation("pickup"),
    emptyLocation("delivery"),
  ]);
  const [legDescriptions, setLegDescriptions] = useState([""]);
  const [truckType, setTruckType] = useState("");
  const [firstname, setFirstname] = useState(cookie.load("ctName") || "");
  const [lastname, setLastname] = useState("");
  const [email, setEmail] = useState("");
  const [mobilenumber, setMobilenumber] = useState("");
  const [profileLoaded, setProfileLoaded] = useState(false);
  const profileOriginal = useRef({ firstname: "", lastname: "", mobilenumber: "" });
  const [collectiondate, setCollectiondate] = useState(todayIso());
  const [collectiontime, setCollectiontime] = useState("10:00");
  const [runners, setRunners] = useState(0);
  const [floors, setFloors] = useState(0);
  const [loading, setLoading] = useState(false);
  const [apierror, setApierror] = useState("");
  const [fullQuote, setFullQuote] = useState(null);
  const [orderid, setOrderid] = useState("");
  const [totalLegs, setTotalLegs] = useState(0);
  const [successOpen, setSuccessOpen] = useState(false);
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [vehiclePickerOpen, setVehiclePickerOpen] = useState(true);
  const [openVehicleGroups, setOpenVehicleGroups] = useState(() =>
    Object.fromEntries(VEHICLE_GROUPS.map((g) => [g.title, false]))
  );
  const routeSectionRef = useRef(null);
  const vehiclePickerRef = useRef(null);

  useEffect(() => {
    axios
      .get(baseURL + "/vehicle-types")
      .then((res) => {
        const list = bookableVehicleTypes(res.data?.vehicles);
        setVehicleTypes(list);
      })
      .catch(() => setVehicleTypes([]));
  }, []);

  useEffect(() => {
    const sessionId = cookie.load("session-id-qt");
    if (!sessionId) {
      setProfileLoaded(true);
      return;
    }

    axios
      .get(baseURL + "/customer-profile", { headers: authHeaders() })
      .then((res) => {
        const profile = res.data || {};
        const fn = profile.firstname || cookie.load("ctName") || "";
        const ln = profile.lastname || "";
        const em = profile.email || "";
        const mob = profile.mobilenumber || "";

        setFirstname(fn);
        setLastname(ln);
        setEmail(em);
        setMobilenumber(mob);
        profileOriginal.current = { firstname: fn, lastname: ln, mobilenumber: mob };

        if (profile.customerid) {
          cookie.save("localsessionid", profile.customerid, {
            path: "/",
            maxAge: 86400,
            secure: true,
          });
        }
        if (fn) {
          cookie.save("ctName", fn, { path: "/", maxAge: 86400, secure: true });
        }
        setProfileLoaded(true);
      })
      .catch(() => setProfileLoaded(true));
  }, []);

  const resetQuote = () => setFullQuote(null);

  const updateLocation = (id, patch) => {
    setLocations((prev) => prev.map((loc) => (loc.id === id ? { ...loc, ...patch } : loc)));
    resetQuote();
  };

  const addStop = () => {
    setLocations((prev) => {
      const next = [...prev];
      next.splice(next.length - 1, 0, emptyLocation("waypoint"));
      return next;
    });
    setLegDescriptions((prev) => {
      const next = [...prev];
      next.splice(next.length - 1, 0, "");
      return next;
    });
    resetQuote();
  };

  const removeStop = (id) => {
    const idx = locations.findIndex((loc) => loc.id === id);
    if (idx <= 0 || idx >= locations.length - 1) return;
    setLocations((prev) => prev.filter((loc) => loc.id !== id));
    setLegDescriptions((prev) => prev.filter((_, i) => i !== idx - 1));
    resetQuote();
  };

  const moveStop = (id, direction) => {
    const idx = locations.findIndex((loc) => loc.id === id);
    if (idx <= 0 || idx >= locations.length - 1) return;

    const swapWith = direction === "up" ? idx - 1 : idx + 1;
    if (swapWith <= 0 || swapWith >= locations.length - 1) return;

    setLocations((prev) => swapPair(prev, idx, swapWith));
    setLegDescriptions((prev) => {
      const legA = Math.min(idx, swapWith);
      const legB = Math.max(idx, swapWith);
      return swapPair(prev, legA, legB);
    });
    resetQuote();
  };

  const updateLegDescription = (index, value) => {
    setLegDescriptions((prev) => prev.map((d, i) => (i === index ? value : d)));
    resetQuote();
  };

  const validAddresses = () =>
    locations.every((loc) => loc.lat && loc.lng) &&
    locations.length >= 2 &&
    !(
      locations[0].lat === locations[locations.length - 1].lat &&
      locations[0].lng === locations[locations.length - 1].lng
    );

  const legsComplete = () =>
    legDescriptions.length === locations.length - 1 &&
    legDescriptions.every((d) => String(d || "").trim().length > 0);

  const buildQuotePayload = () => {
    const collection = locationPayload(locations[0]);
    const delivery = locationPayload(locations[locations.length - 1]);
    const stops = locations.slice(1, -1).map((loc) => ({
      ...locationPayload(loc),
      type: loc.stopType,
    }));
    const summaryDescription = legDescriptions
      .map((d) => String(d || "").trim())
      .filter(Boolean)
      .join("; ");

    return {
      customerid: cookie.load("localsessionid"),
      truckType,
      collection,
      delivery,
      stops,
      legDescriptions,
      category: "multi-stop",
      runners,
      floors,
      description: summaryDescription || "Multi-stop route",
      collectiondate,
      collectiontime,
      prospectemail: email,
      prospectmobilenumber: mobilenumber,
      source_: util.navigator_() + util.source_() + " multi-stop-book",
      trips: 0,
    };
  };

  const getQuote = () => {
    if (!truckType) {
      setApierror("Choose a vehicle before getting a quote.");
      setVehiclePickerOpen(true);
      setOpenVehicleGroups(
        Object.fromEntries(VEHICLE_GROUPS.map((g) => [g.title, false]))
      );
      window.requestAnimationFrame(() => {
        const el = vehiclePickerRef.current;
        if (!el) return;
        const top = el.getBoundingClientRect().top + window.scrollY - 88;
        window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
      });
      return;
    }
    if (!validAddresses()) {
      setApierror("Enter all stop addresses on your route.");
      return;
    }
    if (!legsComplete()) {
      setApierror("Describe what moves on each driver leg before getting a quote.");
      return;
    }
    setLoading(true);
    setApierror("");
    setFullQuote(null);

    axios
      .post(baseURL + "/quote", buildQuotePayload())
      .then((res) => {
        setLoading(false);
        if (res.data?.sellingPrice > 0) {
          setFullQuote({
            tripDistance: res.data.tripDistance,
            tripDuration: res.data.tripDuration,
            sellingPrice: res.data.sellingPrice,
            storedQuoteId: res.data.quoteId,
            stops: res.data.stops || [],
            legs: res.data.legs || [],
            stopCount: res.data.stopCount || locations.length,
            locationCount: res.data.locationCount || res.data.stopCount || locations.length,
            legCount: res.data.legCount || Math.max(1, locations.length - 1),
            stopFeeCents: res.data.stopFeeCents || 0,
          });
        } else {
          setApierror("Could not price this route. Check addresses and try again.");
        }
      })
      .catch(() => {
        setLoading(false);
        setApierror("Quote failed. Check addresses and try again.");
      });
  };

  const maybeUpdateProfile = async () => {
    const sessionId = cookie.load("session-id-qt");
    if (!sessionId) return;

    const orig = profileOriginal.current;
    const changed =
      firstname !== orig.firstname ||
      lastname !== orig.lastname ||
      mobilenumber !== orig.mobilenumber;

    if (!changed) return;

    await axios.post(
      baseURL + "/customer-profile-update",
      { firstname, lastname, email, mobilenumber },
      { headers: authHeaders() }
    );

    cookie.save("ctName", firstname, { path: "/", maxAge: 86400, secure: true });
    profileOriginal.current = { firstname, lastname, mobilenumber };
  };

  const acceptQuote = async () => {
    if (!fullQuote?.storedQuoteId) return;
    setLoading(true);
    setApierror("");

    try {
      await maybeUpdateProfile();
    } catch {
      setLoading(false);
      setApierror("Could not update your profile. Please try again.");
      return;
    }

    axios
      .post(baseURL + "/accept-quote", {
        fullQuote,
        firstname,
        lastname,
        email,
        mobilenumber,
        collectiondate,
        collectiontime,
        description: legDescriptions
          .map((d) => String(d || "").trim())
          .filter(Boolean)
          .join("; "),
        acceptedOffer: "sellingprice",
        customerid: cookie.load("localsessionid"),
      })
      .then((res) => {
        setLoading(false);
        if (res.status === 200 && res.data?.orderid) {
          setOrderid(res.data.orderid);
          setTotalLegs(res.data.totalLegs || res.data.legOrderIds?.length || 0);
          cookie.save("currentOrder", `email=${email}&orderid=${res.data.orderid}`, {
            path: "/",
            maxAge: 86400,
            secure: true,
          });
          setSuccessOpen(true);
        } else {
          setApierror("Booking failed. Please try again.");
        }
      })
      .catch(() => {
        setLoading(false);
        setApierror("Booking failed. Please try again.");
      });
  };

  const canBook =
    profileLoaded &&
    fullQuote?.storedQuoteId &&
    firstname &&
    lastname &&
    email &&
    mobilenumber &&
    legsComplete() &&
    collectiondate &&
    collectiontime;

  const locationCount = locations.length;
  const legCount = Math.max(1, locationCount - 1);
  const selectedVehicle =
    vehicleTypes.find((v) => vehicleTypeSlug(v) === truckType) || null;

  const toggleVehicleGroup = (title) => {
    setOpenVehicleGroups((prev) => ({ ...prev, [title]: !prev[title] }));
  };

  const scrollToRouteSection = () => {
    window.requestAnimationFrame(() => {
      const el = routeSectionRef.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top + window.scrollY - 88;
      window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
    });
  };

  const selectVehicle = (slug) => {
    setTruckType(slug);
    resetQuote();
    setVehiclePickerOpen(false);
    setOpenVehicleGroups((prev) =>
      Object.fromEntries(Object.keys(prev).map((key) => [key, false]))
    );
    scrollToRouteSection();
  };

  const reopenVehiclePicker = () => {
    setVehiclePickerOpen(true);
    setOpenVehicleGroups(Object.fromEntries(VEHICLE_GROUPS.map((g) => [g.title, false])));
    window.requestAnimationFrame(() => {
      const el = vehiclePickerRef.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top + window.scrollY - 88;
      window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
    });
  };

  const renderVehiclePicker = () => (
    <div className="msb-card" style={{ marginBottom: 24 }} ref={vehiclePickerRef}>
      <div className="msb-card-header">
        <h2 className="msb-card-title">Choose your vehicle</h2>
        <p className="msb-card-subtitle">
          Pick the size and body type for the full multi-stop route. Same vehicle covers every driver leg.
        </p>
      </div>
      <div className="msb-card-body">
        {vehicleTypes.length === 0 ? (
          <p className="msb-vehicle-loading">Loading vehicle options…</p>
        ) : !vehiclePickerOpen && selectedVehicle ? (
          <button
            type="button"
            className="msb-vehicle-summary"
            onClick={reopenVehiclePicker}
          >
            <img
              src={vehicleImageUrl(truckType)}
              alt={vehicleOptionLabel(selectedVehicle)}
            />
            <div className="msb-vehicle-summary-copy">
              <div className="msb-vehicle-summary-name">
                {vehicleOptionLabel(selectedVehicle)}
              </div>
              <div className="msb-vehicle-summary-meta">
                {[
                  vehicleCapacityLabel(selectedVehicle),
                  vehicleBodyKind(selectedVehicle),
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </div>
            </div>
            <span className="msb-vehicle-summary-action">
              Change
              <i className="mdi mdi-chevron-down" />
            </span>
          </button>
        ) : (
          VEHICLE_GROUPS.map((group) => {
            const items = vehicleTypes.filter(group.filter);
            if (!items.length) return null;
            const isOpen = openVehicleGroups[group.title] !== false;
            const selectedInGroup = items.some((v) => vehicleTypeSlug(v) === truckType);
            return (
              <div
                key={group.title}
                className={`msb-vehicle-group${isOpen ? " is-open" : ""}${
                  selectedInGroup ? " has-selected" : ""
                }`}
              >
                <button
                  type="button"
                  className="msb-vehicle-group-toggle"
                  onClick={() => toggleVehicleGroup(group.title)}
                  aria-expanded={isOpen}
                >
                  <span className="msb-vehicle-group-title">
                    <i className="mdi mdi-truck" />
                    {group.title}
                    <span className="msb-vehicle-group-count">{items.length}</span>
                  </span>
                  <i
                    className={`mdi ${isOpen ? "mdi-chevron-up" : "mdi-chevron-down"}`}
                  />
                </button>
                {isOpen && (
                  <div className="msb-vehicle-grid">
                    {items.map((v) => {
                      const slug = vehicleTypeSlug(v);
                      const selected = slug === truckType;
                      const capacity = vehicleCapacityLabel(v);
                      const body = vehicleBodyKind(v);
                      const examples = vehicleTipExamples(v);
                      return (
                        <button
                          key={slug}
                          type="button"
                          className={`msb-vehicle-card${selected ? " is-selected" : ""}`}
                          onClick={() => selectVehicle(slug)}
                        >
                          <div className="msb-vehicle-image">
                            <img
                              src={vehicleImageUrl(slug)}
                              alt={vehicleOptionLabel(v)}
                            />
                          </div>
                          <div className="msb-vehicle-name">{vehicleShortName(v)}</div>
                          <div className="msb-vehicle-meta">
                            {capacity && <span>{capacity}</span>}
                            {body && <span>{body}</span>}
                          </div>
                          {examples && (
                            <div className="msb-vehicle-examples">{examples}</div>
                          )}
                          {selected && (
                            <span className="msb-vehicle-selected-tag">
                              <i className="mdi mdi-check-circle" />
                              Selected
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );

  const renderLocationFields = (loc, index) => {
    const isIntermediate = index > 0 && index < locations.length - 1;

    return (
      <div className={`msb-stop-card${loc.lat ? " msb-stop-card--filled" : ""}`}>
        <div className="msb-stop-head">
          <span
            className={`msb-stop-label${
              index === 0
                ? " msb-stop-label--pickup"
                : index === locations.length - 1
                  ? " msb-stop-label--delivery"
                  : ""
            }`}
          >
            {locationLabel(loc, index, locations.length)}
          </span>
          {isIntermediate && (
            <div className="msb-stop-actions">
              {index > 1 && (
                <button
                  type="button"
                  className="msb-stop-move"
                  onClick={() => moveStop(loc.id, "up")}
                  title="Visit this stop earlier on the route"
                >
                  <i className="mdi mdi-arrow-up" />
                  Earlier
                </button>
              )}
              {index < locations.length - 2 && (
                <button
                  type="button"
                  className="msb-stop-move"
                  onClick={() => moveStop(loc.id, "down")}
                  title="Visit this stop later on the route"
                >
                  <i className="mdi mdi-arrow-down" />
                  Later
                </button>
              )}
              <button type="button" className="msb-stop-remove" onClick={() => removeStop(loc.id)}>
                Remove
              </button>
            </div>
          )}
        </div>

        <PlacesAutocomplete
          type="text"
          className="msb-form-input"
          placeholder={
            index === 0
              ? "Where should we collect?"
              : index === locations.length - 1
                ? "Final drop-off address"
                : "Stop address"
          }
          defaultValue={loc.address}
          onPlaceSelected={(place) => {
            updateLocation(loc.id, {
              lat: place?.geometry?.location?.lat(),
              lng: place?.geometry?.location?.lng(),
              address: place?.formatted_address || "",
            });
          }}
        />

        {isIntermediate && (
          <div className="msb-stop-type-row">
            {STOP_TYPES.map((t) => (
              <button
                key={t.value}
                type="button"
                className={`msb-stop-type${loc.stopType === t.value ? " msb-stop-type--active" : ""}`}
                onClick={() => updateLocation(loc.id, { stopType: t.value })}
              >
                {t.label}
              </button>
            ))}
          </div>
        )}

        <div className="msb-contact-row">
          <input
            type="text"
            className="msb-form-input"
            placeholder="Contact name at this stop"
            value={loc.contactName}
            onChange={(e) => updateLocation(loc.id, { contactName: e.target.value })}
          />
          <input
            type="tel"
            className="msb-form-input"
            placeholder="Contact phone"
            value={loc.contactPhone}
            onChange={(e) => updateLocation(loc.id, { contactPhone: e.target.value })}
          />
        </div>

        <input
          type="text"
          className="msb-form-input"
          style={{ marginTop: 8 }}
          placeholder="Access notes (gate code, unit, floor, etc.)"
          value={loc.accessNotes}
          onChange={(e) => updateLocation(loc.id, { accessNotes: e.target.value })}
        />
      </div>
    );
  };

  const renderLegCard = (legIndex) => {
    const from = locations[legIndex];
    const to = locations[legIndex + 1];

    return (
      <div className="msb-leg-card">
        <div className="msb-leg-head">
          <span className="msb-leg-badge">
            <i className="mdi mdi-truck-fast" />
            Driver leg {legIndex + 1} of {legCount}
          </span>
          <span className="msb-leg-route">
            {shortAddress(from.address) || `Stop ${legIndex + 1}`}
            {" → "}
            {shortAddress(to.address) || `Stop ${legIndex + 2}`}
          </span>
        </div>
        <p className="msb-leg-help">
          {legIndex === 0
            ? "First driver job on this route - start time, loading help, and what moves on this leg."
            : "This becomes its own order in the driver app - include load details and anything the driver must know for this segment."}
        </p>

        {legIndex === 0 && (
          <>
            <div className="msb-form-row">
              <div className="msb-form-group">
                <label className="msb-form-label">Date</label>
                <input
                  type="date"
                  className="msb-form-input"
                  value={collectiondate}
                  onChange={(e) => setCollectiondate(e.target.value)}
                />
              </div>
              <div className="msb-form-group">
                <label className="msb-form-label">Start time</label>
                <input
                  type="time"
                  className="msb-form-input"
                  value={collectiontime}
                  onChange={(e) => setCollectiontime(e.target.value)}
                />
              </div>
            </div>
            <div className="msb-form-row">
              <div className="msb-form-group">
                <label className="msb-form-label">Helpers</label>
                <input
                  type="number"
                  min={0}
                  className="msb-form-input"
                  value={runners}
                  onChange={(e) => {
                    setRunners(parseInt(e.target.value, 10) || 0);
                    resetQuote();
                  }}
                />
              </div>
              <div className="msb-form-group">
                <label className="msb-form-label">Floors</label>
                <input
                  type="number"
                  min={0}
                  className="msb-form-input"
                  value={floors}
                  onChange={(e) => {
                    setFloors(parseInt(e.target.value, 10) || 0);
                    resetQuote();
                  }}
                />
              </div>
            </div>
          </>
        )}

        <label className="msb-form-label">What moves on this leg?</label>
        <textarea
          className="msb-form-input"
          rows={2}
          value={legDescriptions[legIndex] || ""}
          onChange={(e) => updateLegDescription(legIndex, e.target.value)}
          placeholder="e.g. 3 office desks from warehouse to client site"
        />
      </div>
    );
  };

  return (
    <div className="msb-page">
      <header className="msb-hero">
        <div className="msb-hero-inner">
          <span className="msb-badge">Multi-stop route</span>
          <h1>One route, multiple driver legs</h1>
          <p>
            Each segment between stops becomes a separate driver job with its own load description and
            site contacts - exactly what drivers need at each pickup and drop-off.
          </p>
          <a className="msb-hero-link" href="/dashboard?booknew=1">
            Simple A→B booking? Book a single trip from your dashboard →
          </a>
        </div>
      </header>

      <div className="msb-layout">
        <div className="msb-main">
          {renderVehiclePicker()}

          <div className="msb-card" ref={routeSectionRef} id="msb-route-section">
            <div className="msb-card-header">
              <h2 className="msb-card-title">Route & driver legs</h2>
              <p className="msb-card-subtitle">
                Add locations in visit order. Use Earlier / Later on intermediate stops to reorder.
                Between each pair, define the driver leg - goods, contacts, and access notes per stop.
              </p>
            </div>
            <div className="msb-card-body">
              <div className="msb-route">
                <div className="msb-route-step msb-route-step--pickup">
                  <div className="msb-route-marker">1</div>
                  {renderLocationFields(locations[0], 0)}
                </div>

                {Array.from({ length: legCount }, (_, legIndex) => (
                  <React.Fragment key={`leg-block-${legIndex}`}>
                    <div className="msb-route-connector msb-route-connector--leg" />
                    {renderLegCard(legIndex)}

                    {legIndex < locations.length - 2 ? (
                      <>
                        <div className="msb-route-connector" />
                        <div className="msb-route-step msb-route-step--waypoint">
                          <div className="msb-route-marker">{legIndex + 2}</div>
                          {renderLocationFields(locations[legIndex + 1], legIndex + 1)}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="msb-route-connector" />
                        <button type="button" className="msb-add-stop" onClick={addStop}>
                          <i className="mdi mdi-plus-circle-outline" />
                          Add another stop
                        </button>
                        <div className="msb-route-connector" />
                        <div className="msb-route-step msb-route-step--delivery">
                          <div className="msb-route-marker">{locationCount}</div>
                          {renderLocationFields(locations[locations.length - 1], locations.length - 1)}
                        </div>
                      </>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>

          <div className="msb-card" style={{ marginTop: 24 }}>
            <div className="msb-card-header">
              <h2 className="msb-card-title">Customer details</h2>
              <p className="msb-card-subtitle">
                Your account profile for quote and confirmation. Site contacts are captured per stop above.
              </p>
            </div>
            <div className="msb-card-body">
              {!profileLoaded ? (
                <p style={{ color: "var(--qt-gray-600)", margin: 0 }}>Loading your profile…</p>
              ) : (
                <>
                  <div className="msb-form-row">
                    <div className="msb-form-group">
                      <label className="msb-form-label">First name</label>
                      <input
                        type="text"
                        className="msb-form-input"
                        placeholder="First name"
                        value={firstname}
                        onChange={(e) => setFirstname(e.target.value)}
                      />
                    </div>
                    <div className="msb-form-group">
                      <label className="msb-form-label">Last name</label>
                      <input
                        type="text"
                        className="msb-form-input"
                        placeholder="Last name"
                        value={lastname}
                        onChange={(e) => setLastname(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="msb-form-group">
                    <label className="msb-form-label">Email</label>
                    <input
                      type="email"
                      className="msb-form-input"
                      placeholder="you@email.com"
                      value={email}
                      readOnly
                    />
                  </div>
                  <div className="msb-form-group">
                    <label className="msb-form-label">Mobile</label>
                    <input
                      type="tel"
                      className="msb-form-input"
                      placeholder="082 123 4567"
                      value={mobilenumber}
                      onChange={(e) => setMobilenumber(e.target.value)}
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        <aside className="msb-sidebar">
          <div className="msb-card msb-quote-panel">
            {apierror && <div className="msb-error">{apierror}</div>}

            {fullQuote ? (
              <>
                <div className="msb-quote-price">
                  <div className="msb-quote-price-value">
                    <CurrencyFormat
                      value={(fullQuote.sellingPrice || 0) / 100}
                      displayType="text"
                      thousandSeparator
                      prefix="R "
                      decimalScale={2}
                      fixedDecimalScale
                    />
                  </div>
                  <div className="msb-quote-price-note">
                    All-inclusive · {fullQuote.locationCount || locationCount} locations ·{" "}
                    {fullQuote.legCount || legCount} driver {legCount === 1 ? "leg" : "legs"}
                  </div>
                </div>

                <div className="msb-quote-meta">
                  {selectedVehicle && (
                    <div className="msb-quote-vehicle">
                      <img
                        src={vehicleImageUrl(truckType)}
                        alt={vehicleOptionLabel(selectedVehicle)}
                      />
                      <div>
                        <div className="msb-quote-vehicle-name">
                          {vehicleOptionLabel(selectedVehicle)}
                        </div>
                        <div className="msb-quote-vehicle-meta">
                          {[
                            vehicleCapacityLabel(selectedVehicle),
                            vehicleBodyKind(selectedVehicle),
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="msb-quote-meta-row">
                    <span className="msb-quote-meta-label">Locations</span>
                    <span className="msb-quote-meta-value">
                      {fullQuote.locationCount || locationCount}
                    </span>
                  </div>
                  <div className="msb-quote-meta-row">
                    <span className="msb-quote-meta-label">Driver legs</span>
                    <span className="msb-quote-meta-value">{fullQuote.legCount || legCount}</span>
                  </div>
                  <div className="msb-quote-meta-row">
                    <span className="msb-quote-meta-label">Distance</span>
                    <span className="msb-quote-meta-value">
                      {((fullQuote.tripDistance || 0) / 1000).toFixed(1)} km
                    </span>
                  </div>
                  {(fullQuote.stopFeeCents || 0) > 0 && (
                    <div className="msb-quote-meta-row">
                      <span className="msb-quote-meta-label">Stop fees</span>
                      <span className="msb-quote-meta-value">
                        R {((fullQuote.stopFeeCents || 0) / 100).toFixed(2)}
                      </span>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="msb-quote-empty">
                <div className="msb-quote-empty-icon">
                  <i className="mdi mdi-map-marker-path" />
                </div>
                <p>
                  Complete each stop, define every driver leg, then get one combined price for the full
                  route.
                </p>
              </div>
            )}

            <div className="msb-actions">
              <button
                type="button"
                className="msb-btn"
                disabled={loading || !truckType || !validAddresses() || !legsComplete()}
                onClick={getQuote}
              >
                {loading && !fullQuote ? (
                  <>
                    <span className="msb-spinner" />
                    Getting quote…
                  </>
                ) : fullQuote ? (
                  "Refresh quote"
                ) : (
                  <>
                    Get multi-stop quote
                    <i className="mdi mdi-calculator" />
                  </>
                )}
              </button>

              {fullQuote && (
                <button
                  type="button"
                  className="msb-btn msb-btn-book"
                  disabled={loading || !canBook}
                  onClick={acceptQuote}
                >
                  {loading ? (
                    <>
                      <span className="msb-spinner" />
                      Booking…
                    </>
                  ) : (
                    <>
                      Book {legCount} driver {legCount === 1 ? "leg" : "legs"} -{" "}
                      <CurrencyFormat
                        value={(fullQuote.sellingPrice || 0) / 100}
                        displayType="text"
                        thousandSeparator
                        prefix="R "
                        decimalScale={2}
                        fixedDecimalScale
                      />
                    </>
                  )}
                </button>
              )}
            </div>

            {(!validAddresses() || !legsComplete()) && !fullQuote && (
              <p style={{ fontSize: 13, color: "var(--qt-gray-600)", marginTop: 16, marginBottom: 0 }}>
                Add all addresses and describe what moves on each driver leg.
              </p>
            )}
          </div>
        </aside>
      </div>

      <Modal isOpen={successOpen} className="msb-success-modal" centered>
        <ModalHeader toggle={() => setSuccessOpen(false)}>Multi-stop route booked</ModalHeader>
        <ModalBody>
          <p>
            Your route created {totalLegs || legCount} driver legs. Each leg is a separate job card
            with its own load description and site contacts.
          </p>
          <span className="msb-success-order">#{orderid?.split("-")[0]}</span>
        </ModalBody>
        <ModalFooter>
          <Button
            color="primary"
            onClick={() => {
              window.location.href =
                TRACK_BASE +
                "?paynow=yes&orderid=" +
                encodeURIComponent(orderid) +
                "&email=" +
                encodeURIComponent(email);
            }}
          >
            Track order
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
